import React, { useState } from 'react';
import { ArrowRight, Clock, FileText, Briefcase, Home, Users, CheckCircle, Star, Globe, Heart, Award, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import ApplicationForm from '../components/ApplicationForm';

const NursingInGermanyPage = () => {
  const [applyFormOpen, setApplyFormOpen] = useState(false);

  const processSteps = [
    {
      icon: Globe,
      title: "German Language Training",
      description: "Enroll in certified German language institutions up to B2 level",
      duration: "8-10 months",
      color: "from-navy-700 to-navy-900"
    },
    {
      icon: FileText,
      title: "Document Processing",
      description: "Translation and submission of degree certificates to German authorities",
      duration: "1 month",
      color: "from-brand-500 to-navy-900"
    },
    {
      icon: Briefcase,
      title: "Job Placement",
      description: "Secure job contracts from reputed hospitals and clinics in Germany",
      duration: "Ongoing support",
      color: "from-brand-500 to-brand-600"
    },
    {
      icon: Home,
      title: "Relocation Support",
      description: "Accommodation assistance and family relocation support",
      duration: "Complete support",
      color: "from-navy-600 to-navy-900"
    }
  ];

  const benefits = [
    "High demand with 200,000 vacancies expected by 2030",
    "Competitive salaries and excellent benefits",
    "World-class healthcare system experience",
    "Family relocation support included",
    "Permanent residency pathway available",
    "Professional growth opportunities"
  ];

  const stats = [
    { value: "200K+", label: "Job Vacancies by 2030", icon: TrendingUp },
    { value: "€3,500+", label: "Average Monthly Salary", icon: Award },
    { value: "8-10", label: "Months Language Training", icon: Clock },
    { value: "100%", label: "Job Placement Support", icon: Briefcase }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-navy-50 via-white to-brand-50 pt-24 pb-16">
        <div className="absolute inset-0 opacity-40">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-navy-100 text-navy-800 rounded-full text-sm font-medium mb-6">
                <Heart className="w-4 h-4 mr-2" />
                Healthcare Career Opportunity
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Nursing Career in
                <span className="block bg-gradient-to-r from-navy-800 to-navy-950 bg-clip-text text-transparent">
                  Germany
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Build a rewarding and secure future in Germany's world-class healthcare system. 
                Complete guidance from language training to job placement and family relocation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  onClick={() => setApplyFormOpen(true)}
                  className="bg-gradient-to-r from-navy-800 to-navy-950 hover:from-navy-800 hover:to-navy-900 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  Start Your Journey
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <a 
                  href="/docs/Xpert Advisor-Nursing-In-Germany.pdf"
                  download="Xpert Advisor-Nursing-In-Germany.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    variant="outline" 
                    className="border-2 border-navy-900 text-brand-600 hover:bg-navy-50 px-8 py-6 text-lg"
                  >
                    Download Brochure
                  </Button>
                </a>
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4">
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                    <div className="flex items-center mb-2">
                      <stat.icon className="w-5 h-5 text-brand-600 mr-2" />
                      <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
                    </div>
                    <p className="text-sm text-gray-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right Content - Visual */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Professional Nurse in Germany"
                  className="w-full h-[500px] xl:h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/50 to-transparent"></div>
                
                {/* Floating Badge */}
                <div className="absolute top-6 right-6 bg-brand-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                  High Demand Career
                </div>
                
                {/* Bottom Info Cards */}
                <div className="absolute bottom-6 left-6 right-6 grid grid-cols-2 gap-4">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <Globe className="w-6 h-6 text-brand-600 mb-2" />
                    <p className="text-sm font-medium text-gray-900">B2 German</p>
                    <p className="text-xs text-gray-600">Language Support</p>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <Users className="w-6 h-6 text-brand-600 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Family Support</p>
                    <p className="text-xs text-gray-600">Relocation Help</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-navy-700 to-brand-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</h3>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Your Journey to <span className="text-brand-600">Germany</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We guide you through every step of your nursing career transition to Germany
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Connector Line */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-1/2 w-full h-0.5 bg-gradient-to-r from-navy-200 to-brand-200 z-0"></div>
                )}

                <div className="relative z-10 bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  {/* Step Number */}
                  <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-br from-navy-700 to-brand-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>

                  {/* Icon */}
                  <div className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center mb-4`}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 mb-4">{step.description}</p>
                  
                  {/* Duration */}
                  <div className="flex items-center text-sm text-brand-600 font-medium">
                    <Clock className="w-4 h-4 mr-2" />
                    {step.duration}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories Image Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Image */}
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1582750433449-648ed127bb54?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Successful Nurses in Germany"
                className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/50 to-transparent rounded-2xl"></div>
              
              {/* Floating Success Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mr-4">
                    <Star className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Success Stories</h3>
                    <p className="text-sm text-gray-600">500+ nurses placed successfully</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Content */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Join Our Success Stories
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Our comprehensive support system has helped hundreds of Indian nurses successfully transition to rewarding careers in Germany's healthcare system.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center p-4 bg-navy-50 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-brand-600 mr-3" />
                  <span className="text-gray-700">Complete language training support</span>
                </div>
                <div className="flex items-center p-4 bg-brand-50 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-brand-600 mr-3" />
                  <span className="text-gray-700">Guaranteed job placement assistance</span>
                </div>
                <div className="flex items-center p-4 bg-navy-50 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-brand-600 mr-3" />
                  <span className="text-gray-700">Family relocation and settlement support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-navy-50 to-brand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Benefits List */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose <span className="text-brand-600">Germany?</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Germany offers exceptional opportunities for qualified nurses with world-class healthcare infrastructure and career growth.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-brand-500 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700 text-lg">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Image and Highlight Card */}
            <div className="space-y-8">
              {/* Hospital Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Professional Nurses in Germany"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-bold">Professional Nursing Team</h3>
                  <p className="text-sm opacity-90">Excellence in Patient Care</p>
                </div>
              </div>

              {/* Highlight Card */}
              <div className="bg-white rounded-3xl p-8 shadow-2xl">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-brand-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Critical Shortage</h3>
                  <p className="text-gray-600">Germany faces a severe nursing shortage</p>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-brand-50 rounded-2xl p-6 mb-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-red-600 mb-2">200,000</div>
                    <p className="text-gray-700 font-medium">Expected vacancies by 2030</p>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-gray-600 mb-4">This creates excellent opportunities for qualified international nurses</p>
                  <Button 
                    onClick={() => setApplyFormOpen(true)}
                    className="bg-gradient-to-r from-red-500 to-brand-500 hover:from-red-600 hover:to-brand-600 text-white px-6 py-3"
                  >
                    Seize This Opportunity
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-navy-800 to-navy-950 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt="Nursing Team"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your German Nursing Career?
          </h2>
          <p className="text-xl text-navy-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of Indian nurses who have successfully transitioned to rewarding careers in Germany's healthcare system.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => setApplyFormOpen(true)}
              className="bg-white text-brand-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-xl"
            >
              Apply Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-brand-600 px-8 py-6 text-lg"
            >
              Schedule Consultation
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      
      {/* Application Form Modal */}
      <ApplicationForm open={applyFormOpen} onOpenChange={setApplyFormOpen} />
    </div>
  );
};

export default NursingInGermanyPage;